import React, { useCallback } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { trackFieldFocus } from '@/domain/job-application/helpers';
import { LOFormFieldValues } from '@/domain/job-application/types';

import * as S from './form-fields.common.styles';

type Props = {
  control: Control<LOFormFieldValues>;
  name: 'firstName' | 'lastName';
};

const NAME_FIELD_MIN_LENGTH = 2;
const NAME_FIELD_MAX_LENGTH = 49;

const validateName = (value: string): boolean => value.trim().length >= NAME_FIELD_MIN_LENGTH;

export const NameField: React.VFC<Props> = ({ control, name }) => {
  const intl = useIntl();
  const copyKey = `JOBS_POSTING_APPLY_FORM_LO_${name === 'firstName' ? 'FIRST' : 'LAST'}_NAME_`;

  const onFieldFocus = useCallback(() => {
    trackFieldFocus({
      isAutofilled: false,
      isMandatory: true,
      fieldName: name === 'firstName' ? 'first_name' : 'last_name',
    });
  }, [name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...fieldProps }, fieldState }) => (
        <S.FormField
          size="medium"
          maxLength={NAME_FIELD_MAX_LENGTH}
          data-cy={`${name === 'firstName' ? 'first' : 'last'}-name`}
          label={intl.formatMessage({
            id: `${copyKey}LABEL`,
          })}
          innerRef={ref}
          {...fieldProps}
          error={!!fieldState.error}
          helperText={fieldState?.error ? intl.formatMessage({ id: `${copyKey}FIELD_ERROR` }) : null}
          onFocus={onFieldFocus}
        />
      )}
      rules={{
        required: true,
        maxLength: NAME_FIELD_MAX_LENGTH,
        validate: validateName,
      }}
    />
  );
};
